import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import McpBindingManager from '../../integrations/McpBindingManager';
import { McpEntry, McpServer } from '../../integrations/mcpServerRegistry';
import {
    Budget,
    COMPLETION_OPTIONS,
    Completion,
    McpConnection,
    NATIVE_TOOL_CATALOG,
    ServiceTools,
} from '../servicesShared';
import { DirtySetter, SectionHeader } from './editorShared';
import { EditableStateField, StateSchemaEditor } from './StateSchemaEditor';
import WritePolicyTable from './WritePolicyTable';

interface AgentOption {
    id: string;
    name: string;
}

export interface BuildTabProps {
    isNew: boolean;
    name: string;
    onNameChange: (value: string) => void;
    previewKey: string;
    keyLocked: boolean;
    instructions: string;
    onInstructionsChange: DirtySetter<string>;
    guardrails: string;
    onGuardrailsChange: DirtySetter<string>;
    tools: ServiceTools;
    onToolsChange: DirtySetter<ServiceTools>;
    servers: McpServer[];
    agents: AgentOption[];
    sourceAgentId: string | null;
    onSourceAgentChange: (agentId: string | null) => void;
    seeding: boolean;
    onSeedFromAgent: (agentId: string) => void;
    stateFields: EditableStateField[];
    onStateFieldsChange: DirtySetter<EditableStateField[]>;
    headline: string;
    onHeadlineChange: DirtySetter<string>;
    completion: Completion;
    onCompletionChange: DirtySetter<Completion>;
    goal: string;
    onGoalChange: DirtySetter<string>;
    budget: Budget;
    onBudgetChange: DirtySetter<Budget>;
}

/** Build: the job definition. What the AI is told, what it may touch, what it reports, whether it ends, what it may spend. */
export const BuildTab: React.FC<BuildTabProps> = (props) => {
    const {
        isNew, name, onNameChange, previewKey, keyLocked,
        instructions, onInstructionsChange, guardrails, onGuardrailsChange,
        tools, onToolsChange, servers, agents, sourceAgentId, onSourceAgentChange, seeding, onSeedFromAgent,
        stateFields, onStateFieldsChange, headline, onHeadlineChange,
        completion, onCompletionChange, goal, onGoalChange, budget, onBudgetChange,
    } = props;

    const toggleNativeTool = (toolName: string, enabled: boolean) =>
        onToolsChange((prev) => ({
            ...prev,
            native: enabled ? Array.from(new Set([...prev.native, toolName])) : prev.native.filter((existing) => existing !== toolName),
        }));

    const setBudgetField = (key: keyof Budget, raw: string) =>
        onBudgetChange((prev) => {
            const next = { ...prev };
            if (raw.trim() === '') delete next[key];
            else (next as any)[key] = key === 'period' ? raw : Number(raw);
            return next;
        });

    return (
        <>
            <section className="mb-10">
                <SectionHeader title="The job" />
                <div className="space-y-4">
                    {isNew && (
                        <div className="space-y-1">
                            <Label htmlFor="service-name">Name</Label>
                            <Input id="service-name" value={name} onChange={(event) => onNameChange(event.target.value)} placeholder="Own warehouse accuracy" />
                            <p className="text-xs text-muted-foreground font-mono">
                                key: {previewKey || '—'}
                                {keyLocked ? ' (locked)' : ''}
                            </p>
                        </div>
                    )}
                    <div className="space-y-1">
                        <Label>Customer agent</Label>
                        <Select value={sourceAgentId || ''} onValueChange={(value) => onSourceAgentChange(value || null)}>
                            <SelectTrigger className="w-[320px]">
                                <SelectValue placeholder="Choose the agent that talks to customers…" />
                            </SelectTrigger>
                            <SelectContent>
                                {agents.map((agent) => (
                                    <SelectItem key={agent.id} value={agent.id}>
                                        {agent.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                            Required to publish. The job never speaks to the customer in its own name: every message goes out through this agent, in
                            chat and by email when the agent has an email channel. The customer's replies come back to the job through the agent.
                        </p>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="service-instructions">Instructions</Label>
                        <Textarea
                            id="service-instructions"
                            value={instructions}
                            onChange={(event) => onInstructionsChange(event.target.value)}
                            placeholder="The job, written to the AI: what it owns for each customer, what good looks like, how it should work each wake, when to ask, when to loop someone in…"
                            rows={14}
                        />
                        <p className="text-xs text-muted-foreground">The job follows these on every wake with the tools granted below. Required to publish.</p>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="service-guardrails">Guardrails</Label>
                        <Textarea
                            id="service-guardrails"
                            value={guardrails}
                            onChange={(event) => onGuardrailsChange(event.target.value)}
                            placeholder="Things it may never do. Rendered separately from the instructions and enforced where possible."
                            rows={4}
                        />
                    </div>
                </div>
            </section>

            <section className="mb-10">
                <SectionHeader
                    title="Tools"
                    description="What the job may access. Credentials stay on the organization server and are minted per call; the sandbox never holds one."
                />
                {sourceAgentId && (
                    <div className="flex items-center gap-2 mb-4">
                        <Button type="button" variant="outline" size="sm" disabled={seeding} onClick={() => onSeedFromAgent(sourceAgentId)}>
                            {seeding ? 'Copying…' : `Copy tool grants from ${agents.find((agent) => agent.id === sourceAgentId)?.name || 'the customer agent'}`}
                        </Button>
                        <span className="text-xs text-muted-foreground">Starts the job with the same MCP and data tools the agent has. Adjust below.</span>
                    </div>
                )}

                <div className="space-y-3 mb-6">
                    <Label className="text-sm">MCP servers</Label>
                    <McpBindingManager
                        entries={tools.mcp as McpEntry[]}
                        onChange={(entries) => onToolsChange((prev) => ({ ...prev, mcp: entries as McpConnection[] }))}
                        consumerLabel="service"
                    />
                </div>

                <div className="space-y-2 mb-6">
                    <Label className="text-sm">Write policy</Label>
                    <p className="text-xs text-muted-foreground">
                        Reads are always allowed. For every write tool, decide whether the job just does it, asks the customer first, or asks
                        your team first (Team tab). Held writes land in Needs you; idempotent receipts stop a retried write from running twice.
                    </p>
                    <WritePolicyTable
                        bindings={tools.mcp}
                        servers={servers}
                        policy={tools.write_policy || {}}
                        onChange={(write_policy) => onToolsChange((prev) => ({ ...prev, write_policy }))}
                    />
                </div>

                <div className="mb-6">
                    <Label className="text-sm">Aimdoc data tools</Label>
                    <p className="text-xs text-muted-foreground mt-1">These only see the job's customer: their sessions, journey and account facts.</p>
                    <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                        {NATIVE_TOOL_CATALOG.map((tool) => (
                            <label key={tool.name} className="flex items-center gap-2 text-sm">
                                <Checkbox checked={tools.native.includes(tool.name)} onCheckedChange={(checked) => toggleNativeTool(tool.name, checked === true)} />
                                <span title={tool.description}>{tool.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="space-y-2">
                    {(
                        [
                            ['code', 'Code execution', 'Run scripts in the job workspace (also what script routines use).'],
                            ['web', 'Web search & fetch', ''],
                            ['knowledge', 'Knowledge base search', ''],
                            ['crm_read', 'CRM read tools', "The customer's own CRM record, account and deals; identity-verified contacts only."],
                        ] as ['code' | 'web' | 'knowledge' | 'crm_read', string, string][]
                    ).map(([key, label, hint]) => (
                        <label key={key} className="flex items-center gap-2 text-sm">
                            <Switch checked={Boolean(tools[key])} onCheckedChange={(checked) => onToolsChange((prev) => ({ ...prev, [key]: checked }))} />
                            <span className="flex flex-col">
                                <span>{label}</span>
                                {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
                            </span>
                        </label>
                    ))}
                </div>
            </section>

            <section className="mb-10">
                <SectionHeader
                    title="State"
                    description="The first-class state the job page shows: a few typed numbers and the headline built from them. The job validates these on set_state and keeps its own working data in free-form details."
                />
                <StateSchemaEditor fields={stateFields} onFieldsChange={onStateFieldsChange} headline={headline} onHeadlineChange={onHeadlineChange} />
            </section>

            <section className="mb-10">
                <SectionHeader title="Completion" description="Whether the AI may ever end this job." />
                <div className="space-y-3 max-w-xl">
                    {COMPLETION_OPTIONS.map((option) => (
                        <label key={option.value} className="flex items-start gap-2 text-sm">
                            <input
                                type="radio"
                                className="mt-1"
                                name="completion"
                                checked={completion === option.value}
                                onChange={() => onCompletionChange(option.value)}
                            />
                            <span className="flex flex-col">
                                <span>{option.label}</span>
                                <span className="text-xs text-muted-foreground">{option.description}</span>
                            </span>
                        </label>
                    ))}
                    {completion === 'ai' && (
                        <div className="space-y-1 pt-1">
                            <Label htmlFor="service-goal">Goal — what “done” means</Label>
                            <Textarea
                                id="service-goal"
                                rows={3}
                                value={goal}
                                onChange={(event) => onGoalChange(event.target.value)}
                                placeholder="e.g. Every warehouse is connected, the first stock sync has reconciled, and the customer has confirmed the picking workflow."
                            />
                            <p className="text-xs text-muted-foreground">Required for a bounded job. The AI ends the job when this is reached or not applicable, with the reason on the record.</p>
                        </div>
                    )}
                </div>
            </section>

            <section className="mb-10">
                <SectionHeader
                    title="Budget"
                    description="Credits per period per job. Running out never turns a job off — it pauses, blocked on budget, until the period resets. Blank = platform defaults (200 target / 400 max per month, 40 per wake)."
                />
                <div className="grid sm:grid-cols-4 gap-4 max-w-2xl">
                    <div className="space-y-1">
                        <Label>Period</Label>
                        <Select value={budget.period || 'month'} onValueChange={(value) => setBudgetField('period', value)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="day">Day</SelectItem>
                                <SelectItem value="week">Week</SelectItem>
                                <SelectItem value="month">Month</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="budget-target">Target</Label>
                        <Input id="budget-target" type="number" min={0} step="any" placeholder="200" value={budget.target_credits ?? ''} onChange={(event) => setBudgetField('target_credits', event.target.value)} />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="budget-max">Maximum</Label>
                        <Input id="budget-max" type="number" min={0} step="any" placeholder="400" value={budget.max_credits ?? ''} onChange={(event) => setBudgetField('max_credits', event.target.value)} />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="budget-wake">Per wake</Label>
                        <Input id="budget-wake" type="number" min={0} step="any" placeholder="40" value={budget.max_credits_per_wake ?? ''} onChange={(event) => setBudgetField('max_credits_per_wake', event.target.value)} />
                    </div>
                </div>
            </section>
        </>
    );
};

export default BuildTab;
